import { inject, Injectable, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
  RouterStateSnapshot,
  Routes,
  UrlTree,
} from '@angular/router';
import { AppLayoutPublicComponent } from './layout/public';
import { AppLayoutSecureComponent } from './layout/secure';
import { AppHomeComponent } from './section/home';
import { AppNotFoundComponent } from './section/not-found';
import { filter, map } from 'rxjs/operators';
import {
  apiAuthGuardWithParams,
  ApiAuthService,
  BackHandlerService,
} from '@app/api';
import { pickBy } from 'lodash';

const checkHome = () => {
  const router = inject(Router);
  const apiAuthSrv = inject(ApiAuthService);
  return apiAuthSrv.userSubject.pipe(
    map((result: any) => {
      if (!apiAuthSrv.hasPermissions('BACKEND_MENU')) {
        if (apiAuthSrv.hasPermissions('BACKEND_TRAVEL_ONLY')) {
          router.navigate(['/travel']);
          return false;
        } else {
          router.navigate(['/not-found']);
          return false;
        }
      } else {
        return true;
      }
    }),
  );
};

export const publicRoutes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('./section/auth').then((m) => m.AppAuthModule),
  },
];

export const secureRoutes: Routes = [
  {
    path: '',
    component: AppHomeComponent,
    canActivate: [checkHome],
  },
  {
    path: 'mail',
    loadChildren: () => import('./section/mail').then((m) => m.AppMailModule),
  },
  {
    path: 'sms',
    loadChildren: () => import('./section/sms').then((m) => m.AppSmsModule),
  },
  {
    path: 'travel',
    loadChildren: () =>
      import('./section/travel').then((m) => m.AppTravelModule),
  },
  {
    path: 'user',
    loadChildren: () => import('./section/user').then((m) => m.AppUserModule),
  },
  {
    path: 'support',
    loadChildren: () =>
      import('./section/support').then((m) => m.AppSupportModule),
  },
  {
    path: 'parameter',
    loadChildren: () =>
      import('./section/parameter').then((m) => m.AppParameterModule),
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./section/profile').then((m) => m.AppProfileModule),
  },
  {
    path: 'weather',
    loadChildren: () =>
      import('./section/weather').then((m) => m.AppWeatherModule),
  },
];

@Injectable({ providedIn: 'root' })
export class PreserveQueryParamsGuard implements CanActivateChild {
  constructor(
    private router: Router,
    public queryParamsService: QueryParamsService,
  ) {}

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    const previousKeep = pickBy(
      this.queryParamsService.getPreviousQueryParams(),
      (value, key) =>
        key != 'Sort' &&
        key != 'Filters' &&
        (key.endsWith('Sort') || key.endsWith('Filters')),
    );
    const currentParams = state.root.queryParams;

    const allParamsAreKept = Object.keys(previousKeep as any).every(
      (key) => currentParams[key] && previousKeep[key] === currentParams[key],
    );

    if (allParamsAreKept) {
      return true;
    }

    return this.router.createUrlTree([state.url.split('?')[0]], {
      queryParams: {
        ...currentParams,
        ...previousKeep,
      },
    });
  }
}

@Injectable({ providedIn: 'root' })
export class QueryParamsService {
  private previousQueryParams: Record<string, string> = {};

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        const urlTree = this.router.parseUrl(this.router.url);
        this.previousQueryParams = { ...urlTree.queryParams };
      });
  }

  getPreviousQueryParams(): Record<string, string> {
    return this.previousQueryParams;
  }
}

export const routes: Routes = [
  {
    path: '',
    component: AppLayoutSecureComponent,
    canActivate: [apiAuthGuardWithParams({ accesses: ['BACKEND_LOGIN'] })],
    canActivateChild: [
      BackHandlerService.canActivateChild,
      PreserveQueryParamsGuard,
    ],
    children: secureRoutes,
  },
  {
    path: '',
    component: AppLayoutPublicComponent,
    children: publicRoutes,
  },
  {
    path: '**',
    component: AppNotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      initialNavigation: 'enabledNonBlocking',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationError))
      .subscribe((event: any) => {
        this.router.navigateByUrl('not-found');
      });
  }
}
